// The journey as a clock and as a sequence of things that happened.
//
// Replay exists to answer where an hour went, which makes it the screen somebody opens when a
// customer disputes an arrival time. Two things follow from that.
//
// It runs on the trip's own clock. A cursor that counted recorded fixes crossed an hour of silence
// in one tick, and a slider measured in samples put its midpoint wherever the fixes happened to be
// dense rather than half way through the journey.
//
// And it says what happened rather than only drawing where. "Stopped 14 minutes", "over the limit",
// "reached Weighbridge 1 at 20:10" are the sentences somebody is arguing about; a moving dot on a
// line is not an answer to any of them.
//
// Nothing here fetches or draws. It is arithmetic over fixes the page already holds, so it can be
// tested without a map.

/** When each fix was recorded, as numbers, in the order they were recorded. */
export const clockOf = (frames) => frames.map((p) => Date.parse(p.fixTime));

/** How long the whole recorded journey lasted. */
export const spanOf = (times) => (times.length > 1 ? times[times.length - 1] - times[0] : 0);

/**
 * Where the trip is at a moment, and how long it has been since anything was recorded.
 *
 * The frame is the most recent fix at or before that moment - never an interpolation between two.
 * A position between fixes is a guess, and this is the screen people use to argue about an hour; a
 * truck standing at its last known place until it reports again is what actually happened.
 *
 * `gapMs` is how long the trail has been silent at that moment. A long one is a hole in the record
 * rather than a stationary vehicle, and the two are indistinguishable on a map unless one says so.
 */
export const cursorAt = (times, elapsedMs) => {
  if (!times.length) return { index: 0, gapMs: 0 };
  const moment = times[0] + Math.max(0, elapsedMs);
  let index = 0;
  while (index + 1 < times.length && times[index + 1] <= moment) index += 1;
  return { index, gapMs: Math.max(0, moment - times[index]) };
};

/**
 * A fix's speed in km/h, whichever unit it arrived in.
 *
 * The tracking provider reports knots and the product converts where it has already read a position;
 * a trail assembled from samples can carry either. Reading only one quietly treats forty knots as
 * forty km/h, which is the difference between within the limit and well over it.
 */
export const speedOf = (frame) => {
  if (frame?.speedKph != null && Number.isFinite(Number(frame.speedKph))) return Number(frame.speedKph);
  if (frame?.speed != null && Number.isFinite(Number(frame.speed))) return Number(frame.speed) * 1.852;
  return null;
};

/** Metres between two fixes, close enough over the few hundred a stop spans. */
const metres = (a, b) => {
  const lat = ((Number(b.latitude) - Number(a.latitude)) * Math.PI) / 180;
  const lon = ((Number(b.longitude) - Number(a.longitude)) * Math.PI) / 180;
  const mid = ((Number(a.latitude) + Number(b.latitude)) / 2) * (Math.PI / 180);
  return Math.hypot(lat, lon * Math.cos(mid)) * 6371000;
};

/** A truck is standing if it has not moved further than this between fixes. */
export const STILL_METRES = 60;
/** Standing shorter than this is a junction or a queue, not a stop worth naming. */
export const STOP_MS = 2 * 60 * 1000;
/** Silence longer than this is a hole in the record rather than the space between fixes. */
export const GAP_MS = 5 * 60 * 1000;
/**
 * How long a tracker may be quiet before two fixes in one place stop being evidence of standing
 * there throughout.
 *
 * A judgement, and worth stating as one. A parked truck reports less often than a moving one, so
 * requiring fixes every few minutes would discard most real stops. But a unit seen at a gate at nine
 * and again at ten has not been observed waiting for an hour - it could have left and come back -
 * and a replay used to settle a dispute must not present that inference as a reading.
 */
export const STILL_OBSERVED_MS = 15 * 60 * 1000;

/**
 * Where the truck stood still, and for how long.
 *
 * Measured from the fixes rather than from the checkpoints, because most of a journey's lost time is
 * spent where no checkpoint stands - a level crossing, a queue outside a gate, a tea stop. Runs of
 * fixes within a short distance of each other are one stop, timed from the first to the last.
 *
 * A run that is also silent is not reported as a stop: a truck that stopped reporting has not been
 * observed standing anywhere, and saying it stood there for forty minutes would be inventing the
 * evidence somebody is about to be shown.
 */
export const stopsIn = (frames, times) => {
  const stops = [];
  // Whether the trail went quiet for longer than two fixes in one place can account for.
  const unobserved = (from, to) => times
    .slice(from + 1, to + 1)
    .some((t, n) => t - times[from + n] > STILL_OBSERVED_MS);

  let start = 0;
  for (let i = 1; i <= frames.length; i += 1) {
    const moved = i === frames.length || metres(frames[start], frames[i]) > STILL_METRES;
    if (moved) {
      const endedAt = i - 1;
      const held = times[endedAt] - times[start];
      if (endedAt > start && held >= STOP_MS && !unobserved(start, endedAt)) {
        stops.push({
          from: start, to: endedAt, startedAt: times[start], ms: held,
        });
      }
      start = i;
    }
  }
  return stops;
};

/**
 * Everything worth telling somebody about the journey, in the order it happened.
 *
 * Each carries the moment it happened as milliseconds from the first fix, so clicking one moves
 * playback to it. Arrivals come from the trip's own recorded stages rather than from geometry: a
 * stage is what the plant wrote down, and that is what a dispute is about.
 */
export const journeyEvents = ({
  frames = [], times = [], detections = [], limitKph = 0,
} = {}) => {
  if (times.length < 2) return [];
  const from = times[0];
  const events = [];

  const halts = stopsIn(frames, times);
  halts.forEach((halt) => {
    events.push({
      key: `stop-${halt.startedAt}`,
      kind: 'STOP',
      at: halt.startedAt - from,
      ms: halt.ms,
      label: 'Halted',
      where: frames[halt.from],
    });
  });

  // A silence, named as one. The truck may have been moving throughout - that is the point of
  // saying it - but not where the silence sits inside a halt we have already reported. There the
  // truck was seen in the same place either side, so calling it both a halt and a hole in the record
  // tells the reader two different things about one stretch of time.
  //
  // Strictly longer than the threshold, as the constant says: a trail reporting exactly every five
  // minutes is reporting normally, not falling silent every time.
  const insideHalt = (fromAt, toAt) => halts.some((halt) => fromAt >= halt.startedAt && toAt <= halt.startedAt + halt.ms);
  times.forEach((t, i) => {
    if (i === 0) return;
    const gap = t - times[i - 1];
    if (gap > GAP_MS && !insideHalt(times[i - 1], t)) {
      events.push({
        key: `gap-${t}`,
        kind: 'GAP',
        at: times[i - 1] - from,
        ms: gap,
        label: 'No GPS data',
        where: frames[i - 1],
      });
    }
  });

  // Over the limit, as one stretch rather than one entry per fix.
  if (limitKph > 0) {
    let open = null;
    frames.forEach((frame, i) => {
      const over = speedOf(frame) > limitKph;
      if (over && open === null) open = i;
      // Closed at the next reading under the limit, or at the end of the trail. A single reading
      // over the limit is a real one: it lasted no measurable time and still happened, so it is
      // reported with no duration rather than discarded for having none.
      const lastFrame = i === frames.length - 1;
      if (open !== null && (!over || lastFrame)) {
        const until = over && lastFrame ? i : i - 1;
        events.push({
          key: `speed-${times[open]}`,
          kind: 'SPEED',
          at: times[open] - from,
          ms: Math.max(0, times[until] - times[open]),
          label: 'Overspeeding',
          peakKph: Math.round(Math.max(...frames.slice(open, until + 1).map((f) => speedOf(f) || 0))),
          where: frames[open],
        });
        open = null;
      }
    });
  }

  detections.forEach((detection) => {
    const when = Date.parse(detection.detectedAt);
    if (!Number.isFinite(when)) return;
    events.push({
      key: `stage-${detection.detectedAt}-${detection.checkpointId}`,
      kind: 'ARRIVED',
      at: when - from,
      label: `Reached ${detection.name || 'a stage'}`,
      when,
    });
  });

  return events.sort((a, b) => a.at - b.at);
};
